import {
  get,
  ref,
  update,
  set,
  onValue,
  type DataSnapshot,
} from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { AuthenticationService } from "../authenticationService/AuthenticationService";
import { NetworkingService } from "../networkingService/NetworkingService";
import { SessionTeardownService } from "../sessionTeardownService/SessionTeardownService";
import { User, UserHelper } from "../../domain/models/User";
import { Gender } from "../../domain/models/Gender";
import { UserConverter } from "./converters/UserConverter";
import {
  UserErrorConverter,
  UserErrorCause,
} from "./converters/UserErrorConverter";
import { CoreError } from "../../domain/models/CoreError";

interface LoadUserOptions {
  suppressMissingProfileReporting?: boolean;
}

export class UserService {
  // Singleton instance
  private static instance: UserService;

  // Private constructor to enforce singleton usage
  private constructor() {}

  // Global accessor to retrieve the singleton
  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * Creates a new user profile in Firebase Realtime Database.
   * @param identifier The user's UID from Firebase Auth
   * @param email The user's email address
   * @returns Promise that resolves with the created User object
   */
  public async createUser(identifier: string, email: string): Promise<User> {
    try {
      const updates: Record<string, unknown> = {};
      updates[`users/${identifier}/profile`] = true;
      updates[`emails/${identifier}`] = email;

      await update(ref(database), updates);

      const user = UserHelper.createEmptyUser(identifier, email);
      return user;
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Loads a user profile from Firebase Realtime Database.
   * @param identifier The user's UID
   * @returns Promise that resolves with the User object
   */
  public async loadUser(
    identifier: string,
    options?: LoadUserOptions,
  ): Promise<User> {
    try {
      const snapshot = await get(ref(database, `users/${identifier}/profile`));

      if (!snapshot.exists()) {
        throw UserErrorConverter.createUserError(
          UserErrorCause.UserNotFound,
          `User with identifier ${identifier} does not exist`,
          {
            reportToSentry: !options?.suppressMissingProfileReporting,
          },
        );
      }

      const data = snapshot.val();
      const user = UserConverter.fromFirebase(data, identifier);

      if (!user) {
        throw UserErrorConverter.createUserError(
          UserErrorCause.InvalidUserData,
          "Failed to parse user data from Firebase",
        );
      }

      return user;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Saves the complete user profile to Firebase.
   * @param user The User object to save
   * @returns Promise that resolves when save is complete
   */
  public async saveUser(user: User): Promise<void> {
    try {
      const updates = UserConverter.toFirebase(user);

      // Ensure email consistency
      if (user.email) {
        updates[`emails/${user.identifier}`] = user.email;
      }

      await update(ref(database), updates);
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Updates the user's full name.
   * @param identifier The user's UID
   * @param name The new full name
   * @returns Promise that resolves when update is complete
   */
  public async updateUserName(identifier: string, name: string): Promise<void> {
    try {
      await set(ref(database, `users/${identifier}/profile/fullName`), name);
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Updates the user's gender.
   * @param identifier The user's UID
   * @param gender The new gender value
   * @returns Promise that resolves when update is complete
   */
  public async updateUserGender(
    identifier: string,
    gender: Gender,
  ): Promise<void> {
    try {
      const updates = UserConverter.partialToFirebase(identifier, { gender });
      await update(ref(database), updates);
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Updates the user's profile photo URL.
   * Note: This method only updates the URL reference.
   * Photo upload to Firebase Storage should be handled separately.
   * @param identifier The user's UID
   * @param photoURL The new profile photo URL
   * @returns Promise that resolves when update is complete
   */
  public async updateUserProfilePhoto(
    identifier: string,
    photoURL: string,
  ): Promise<void> {
    try {
      await set(
        ref(database, `users/${identifier}/profile/profilePhotoURL`),
        photoURL,
      );
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Observes the raw `emailNotifications` preference node. Emits on every
   * change — including the unsubscribe endpoint's server-side write, which is
   * how the Settings toggle stays correct with zero polling.
   */
  public observeEmailNotificationsPreference(
    identifier: string,
    onChanged: (rawValue: unknown) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const preferenceRef = ref(
      database,
      `users/${identifier}/profile/emailNotifications`,
    );
    return onValue(
      preferenceRef,
      (snapshot: DataSnapshot) => onChanged(snapshot.val()),
      (error) => {
        if (
          SessionTeardownService.isTeardownInProgress() ||
          !AuthenticationService.getInstance().isUserLoggedIn
        ) {
          return;
        }
        onError(UserErrorConverter.domainModel(error));
      },
    );
  }

  /**
   * The migration-fallback inputs: this user's notification tokens and their
   * platform tags (web tokens never suppress email).
   */
  public async loadNotificationTokenFacts(
    identifier: string,
  ): Promise<{ tokens: unknown; platforms: unknown }> {
    try {
      const [tokensSnap, platformsSnap] = await Promise.all([
        get(ref(database, `users/${identifier}/profile/notificationTokens`)),
        get(
          ref(
            database,
            `users/${identifier}/profile/notificationTokenPlatforms`,
          ),
        ),
      ]);
      return { tokens: tokensSnap.val(), platforms: platformsSnap.val() };
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Writes the user's explicit email choice. `update()` (not `set`) so the
   * RN-written `autoDisableApplied` marker survives.
   */
  public async setEmailNotificationsEnabled(
    identifier: string,
    enabled: boolean,
  ): Promise<void> {
    try {
      await update(
        ref(database, `users/${identifier}/profile/emailNotifications`),
        {
          enabled,
          source: "user",
          updatedAt: Date.now(),
        },
      );
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Updates partial user data.
   * @param identifier The user's UID
   * @param updates Partial user updates
   * @returns Promise that resolves when update is complete
   */
  public async updateUser(
    identifier: string,
    updates: Partial<User>,
  ): Promise<void> {
    try {
      const firebaseUpdates = UserConverter.partialToFirebase(
        identifier,
        updates,
      );
      await update(ref(database), firebaseUpdates);
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }

  /**
   * Observes changes to a user's profile in real-time.
   * @param identifier The user's UID to observe
   * @param onUserChanged Callback invoked whenever user data changes
   * @param onError Callback invoked when an error occurs
   * @returns Unsubscribe function to stop observing
   */
  public observeUser(
    identifier: string,
    onUserChanged: (user: User) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const userRef = ref(database, `users/${identifier}/profile`);

    const onValueChange = (snapshot: DataSnapshot) => {
      try {
        if (!snapshot.exists()) {
          if (
            SessionTeardownService.isTeardownInProgress() ||
            !AuthenticationService.getInstance().isUserLoggedIn
          ) {
            // Expected while this session's own deletion wipes the profile.
            return;
          }
          // A profile disappearing under a live observer is an account
          // lifecycle event (deletion — a rules regression surfaces as
          // permission-denied instead), so it is the caller's to handle,
          // not a Sentry error.
          onError(
            UserErrorConverter.createUserError(
              UserErrorCause.UserNotFound,
              `User with identifier ${identifier} does not exist`,
              { reportToSentry: false },
            ),
          );
          return;
        }

        const data = snapshot.val();
        const user = UserConverter.fromFirebase(data, identifier);

        if (!user) {
          onError(
            UserErrorConverter.createUserError(
              UserErrorCause.InvalidUserData,
              "Failed to parse user data from Firebase",
            ),
          );
          return;
        }

        onUserChanged(user);
      } catch (error) {
        onError(UserErrorConverter.domainModel(error));
      }
    };

    const onErrorCallback = (error: Error) => {
      if (
        SessionTeardownService.isTeardownInProgress() ||
        !AuthenticationService.getInstance().isUserLoggedIn
      ) {
        return;
      }
      onError(UserErrorConverter.domainModel(error));
    };

    const unsubscribe = onValue(userRef, onValueChange, onErrorCallback);

    // Return unsubscribe function
    return () => {
      unsubscribe();
    };
  }

  /**
   * Deletes the current authenticated user through backend orchestration.
   * The backend callable handles partnership cleanup, profile deletion,
   * and auth user deletion in a single flow.
   * @param identifier The user's UID
   * @returns Promise that resolves when deletion is complete
   */
  public async deleteUser(identifier: string): Promise<void> {
    try {
      if (!identifier) {
        throw UserErrorConverter.createUserError(
          UserErrorCause.InvalidUserData,
          "User identifier is required for account deletion",
        );
      }

      await NetworkingService.getInstance().callCallable<{ status: string }>(
        "deleteUserV2",
      );
    } catch (error) {
      throw UserErrorConverter.domainModel(error);
    }
  }
}
